import React, { useState, useEffect, useRef } from 'react';
import { ref, push, get, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../firebase';
import { UserSelection } from '../types';

interface TeamData {
  bonusPoints: number;
  points: number;
}

const VALID_INVITE_CODES = ['AYAYAITANA', 'WALSHMODE', 'NOWOMENNOCRY'];
const SUBMISSION_DEADLINE = new Date('2024-07-02T16:00:00.000Z'); // July 2nd, 2024, 17:00 BST

const Play: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState('');
  const [availableTeams, setAvailableTeams] = useState<{ [key: string]: TeamData }>({});
  const [selectedRegular, setSelectedRegular] = useState<string[]>([]);
  const [selectedPeace, setSelectedPeace] = useState<string>('');
  const [selectedLove, setSelectedLove] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isRegularDropdownOpen, setIsRegularDropdownOpen] = useState(false);
  const [isPeaceDropdownOpen, setIsPeaceDropdownOpen] = useState(false);
  const [isLoveDropdownOpen, setIsLoveDropdownOpen] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const regularDropdownRef = useRef<HTMLDivElement>(null);
  const peaceDropdownRef = useRef<HTMLDivElement>(null);
  const loveDropdownRef = useRef<HTMLDivElement>(null);

  // Check if deadline has passed
  useEffect(() => {
    const checkDeadline = () => {
      const now = new Date();
      setIsDeadlinePassed(now > SUBMISSION_DEADLINE);
    };
    
    checkDeadline();
    const timer = setInterval(checkDeadline, 1000); // Update every second
    
    return () => clearInterval(timer);
  }, []);

  // Handle invite code validation
  const validateInviteCode = (code: string) => {
    const upperCode = code.toUpperCase();
    if (code === '') {
      setInviteCodeError('');
      return;
    }
    if (!VALID_INVITE_CODES.includes(upperCode)) {
      setInviteCodeError('Invalid invite code');
    } else {
      setInviteCodeError('');
    }
  };

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (regularDropdownRef.current && !regularDropdownRef.current.contains(event.target as Node)) {
        setIsRegularDropdownOpen(false);
      }
      if (peaceDropdownRef.current && !peaceDropdownRef.current.contains(event.target as Node)) {
        setIsPeaceDropdownOpen(false);
      }
      if (loveDropdownRef.current && !loveDropdownRef.current.contains(event.target as Node)) {
        setIsLoveDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load teams from Firebase
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const teamsRef = ref(db, 'teams');
        const snapshot = await get(teamsRef);
        if (!snapshot.exists()) {
          throw new Error('No teams data found');
        }
        const teamsData = snapshot.val();
        setAvailableTeams(teamsData);
      } catch (err) {
        console.error('Error fetching teams:', err);
        setError('Failed to load teams');
      }
    };

    fetchTeams();
  }, []);

  const getAvailableRegularTeams = () => {
    return Object.entries(availableTeams)
      .filter(([team]) => team !== selectedPeace && team !== selectedLove);
  };

  const getAvailablePeaceTeams = () => {
    return Object.entries(availableTeams)
      .filter(([team]) => !selectedRegular.includes(team) && team !== selectedLove);
  };

  const getAvailableLoveTeams = () => {
    return Object.entries(availableTeams)
      .filter(([team]) => !selectedRegular.includes(team) && team !== selectedPeace);
  };

  const handleTeamSelect = (team: string) => {
    if (selectedRegular.length < 3 && !selectedRegular.includes(team)) {
      setSelectedRegular([...selectedRegular, team]);
      setIsRegularDropdownOpen(false);
    }
  };

  const handleTeamRemove = (teamToRemove: string) => {
    setSelectedRegular(selectedRegular.filter(team => team !== teamToRemove));
  };

  // Add new function to check for duplicate email
  const checkDuplicateEmail = async (email: string) => {
    try {
      const selectionsRef = ref(db, 'selections');
      const emailQuery = query(selectionsRef, orderByChild('email'), equalTo(email));
      const snapshot = await get(emailQuery);
      return snapshot.exists();
    } catch (err) {
      console.error('Error checking email:', err);
      throw new Error('Failed to check email');
    }
  };

  // Add new function to check for duplicate userId
  const checkDuplicateUserId = async (userId: string) => {
    try {
      const selectionsRef = ref(db, 'selections');
      const userIdQuery = query(selectionsRef, orderByChild('userId'), equalTo(userId));
      const snapshot = await get(userIdQuery);
      return snapshot.exists();
    } catch (err) {
      console.error('Error checking userId:', err);
      throw new Error('Failed to check userId');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userId || !email || !inviteCode) {
      setError('Please fill in all required fields');
      return;
    }

    if (!VALID_INVITE_CODES.includes(inviteCode.toUpperCase())) {
      setError('Invalid invite code');
      return;
    }

    if (selectedRegular.length !== 3) {
      setError('Please select exactly 3 regular teams');
      return;
    }

    if (!selectedLove) {
      setError('Please select a Love team');
      return;
    }

    if (!selectedPeace) {
      setError('Please select a Peace team');
      return;
    }

    try {
      // Check for duplicate email
      const isEmailTaken = await checkDuplicateEmail(email);
      if (isEmailTaken) {
        setError('This email is already registered');
        return;
      }

      // Check for duplicate userId
      const isUserIdTaken = await checkDuplicateUserId(userId);
      if (isUserIdTaken) {
        setError('This Player ID is already taken');
        return;
      }

      const selection: UserSelection = {
        userId,
        email,
        inviteCode: inviteCode.toUpperCase(),
        selections: {
          regular: selectedRegular,
          peace: selectedPeace,
          love: selectedLove
        }
      };

      await push(ref(db, 'selections'), selection);
      setError('');
      alert('Selection submitted successfully!');
      // Clear form after successful submission
      setUserId('');
      setEmail('');
      setInviteCode('');
      setSelectedRegular([]);
      setSelectedPeace('');
      setSelectedLove('');
    } catch (err) {
      console.error('Submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit selection');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-primary mb-4">She Embrace Cup - WEURO 2025 Game</h1>
        
        <div className="prose prose-lg text-gray-600 mb-8">
          <p>Welcome to a unique prediction game that celebrates women's football and promotes the values of love and peace. 
          Choose your teams with both strategy and heart: support the powerhouses with your regular picks, 
          follow your heart with your Love team, and champion unity with your Peace team! 🌟⚽️🕊️</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Player ID (4-15 characters)</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="block w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 transition-colors duration-200"
                minLength={4}
                maxLength={15}
                required
                placeholder="Enter your player ID"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 transition-colors duration-200"
                required
                placeholder="Enter your email"
              />
              <p className="mt-1 text-sm text-gray-500 italic">You will be contacted if you are the winner ;)</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Invite Code</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => {
                  const newCode = e.target.value;
                  setInviteCode(newCode);
                  validateInviteCode(newCode);
                }}
                className={`block w-full px-4 py-3 rounded-lg border-2 ${
                  inviteCodeError ? 'border-red-500' : 'border-gray-200'
                } focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 transition-colors duration-200`}
                required
                placeholder="Enter your invite code"
              />
              {inviteCodeError && (
                <p className="mt-1 text-sm text-red-600">{inviteCodeError}</p>
              )}
            </div>
          </div>

          {/* Regular Teams Selection */}
          <div className="relative" ref={regularDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose Regular Teams: ({selectedRegular.length}/3)
            </label>
            
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedRegular.map((team) => (
                <div
                  key={team}
                  className="bg-primary/10 text-primary px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium"
                >
                  {team}
                  <button
                    type="button"
                    onClick={() => handleTeamRemove(team)}
                    className="text-primary hover:text-primary/80 focus:outline-none"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setIsRegularDropdownOpen(!isRegularDropdownOpen)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 transition-colors duration-200 text-left flex justify-between items-center"
            >
              <span className="text-gray-700">
                {selectedRegular.length === 3 ? "Max teams selected" : "Select teams"}
              </span>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isRegularDropdownOpen ? 'transform rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isRegularDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {getAvailableRegularTeams().map(([team]) => (
                  <button
                    key={team}
                    type="button"
                    onClick={() => handleTeamSelect(team)}
                    disabled={selectedRegular.length >= 3}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200 ${
                      selectedRegular.includes(team) ? 'bg-gray-50' : ''
                    } ${selectedRegular.length >= 3 && !selectedRegular.includes(team) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span>{team}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Love Team Selection */}
          <div className="relative" ref={loveDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Love Team</label>
            
            {selectedLove && (
              <div className="flex flex-wrap gap-2 mb-2">
                <div className="bg-primary/10 text-primary px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium">
                  {selectedLove}
                  <button
                    type="button"
                    onClick={() => setSelectedLove('')}
                    className="text-primary hover:text-primary/80 focus:outline-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsLoveDropdownOpen(!isLoveDropdownOpen)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 transition-colors duration-200 text-left flex justify-between items-center"
            >
              <span className="text-gray-700">
                {selectedLove ? selectedLove : "Select Love team"}
              </span>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isLoveDropdownOpen ? 'transform rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isLoveDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {getAvailableLoveTeams().map(([team]) => (
                  <button
                    key={team}
                    type="button"
                    onClick={() => {
                      setSelectedLove(team);
                      setIsLoveDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200"
                  >
                    <span>{team}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Peace Team Selection */}
          <div className="relative" ref={peaceDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Peace Team</label>
            
            {selectedPeace && (
              <div className="flex flex-wrap gap-2 mb-2">
                <div className="bg-primary/10 text-primary px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium">
                  {selectedPeace}
                  <button
                    type="button"
                    onClick={() => setSelectedPeace('')}
                    className="text-primary hover:text-primary/80 focus:outline-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsPeaceDropdownOpen(!isPeaceDropdownOpen)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 transition-colors duration-200 text-left flex justify-between items-center"
            >
              <span className="text-gray-700">
                {selectedPeace ? selectedPeace : "Select Peace team"}
              </span>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isPeaceDropdownOpen ? 'transform rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isPeaceDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {getAvailablePeaceTeams().map(([team]) => (
                  <button
                    key={team}
                    type="button"
                    onClick={() => {
                      setSelectedPeace(team);
                      setIsPeaceDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200"
                  >
                    <span>{team}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isDeadlinePassed}
            className={`w-full py-3 px-6 text-white rounded-lg text-lg font-semibold transition-colors duration-200 ${
              isDeadlinePassed 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary hover:bg-primary-dark'
            }`}
          >
            {isDeadlinePassed ? 'Submission Deadline Passed' : 'Submit Selection'}
          </button>
          
          {isDeadlinePassed && (
            <p className="text-red-500 text-center mt-2">
              The submission deadline (July 2nd, 2024, 17:00 BST) has passed.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Play;